import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { Button, CloseIcon, Drawer, Skeleton, Text } from '@mercai/clever'
import { LevelForm } from '@/components'
import { convertObjectToSnakeCase } from '@/helpers'
import { QUERIES_ADMIN, useUpdateProfile } from '@/services'

import { ProfileQuestionsDrawerProps } from './questions-drawer.type'
import styles from './questions-drawer.module.css'
import { useQueryClient } from '@tanstack/react-query'

export const ProfileQuestionsDrawer = ({
  profile,
  questions,
  categorySelected,
  isLoading,
  onUnselectCategory,
}: ProfileQuestionsDrawerProps) => {
  const { uuid } = useParams()
  const { t } = useTranslation('screenProfileShow')
  const queryClient = useQueryClient()

  const [answers, setAnswers] = useState<Record<string, string | string[]>>({})

  const { mutateAsync: updateProfile } = useUpdateProfile()

  const onHandleAnswers = async (key: string, newValue: string | string[]) => {
    const newAttributes = { ...answers, [key]: newValue }

    await updateProfile({
      uuid,
      attributes: newAttributes,
    })

    queryClient.invalidateQueries({
      queryKey: [QUERIES_ADMIN.PROFILE.module, uuid],
    })

    setAnswers(newAttributes)
  }

  useEffect(() => {
    if (profile) {
      const newAnswers = convertObjectToSnakeCase(profile?.attributes)
      setAnswers(newAnswers as Record<string, string | string[]>)
    }
  }, [profile?.attributes])

  const Loading = () => (
    <div className={styles['drawer-wrap']}>
      <div className={styles['drawer-header']}>
        <Skeleton height="5rem" width="100%" />
      </div>

      <div className={styles['loading-content']}>
        <Skeleton height="5rem" width="100%" />
        <Skeleton height="5rem" width="100%" />
        <Skeleton height="5rem" width="100%" />
        <Skeleton height="5rem" width="100%" />
      </div>
    </div>
  )

  return (
    <Drawer
      isOpen={categorySelected !== undefined}
      onHandleOpen={onUnselectCategory}
      className={styles.drawer}
    >
      {isLoading && <Loading />}
      {categorySelected && (
        <div className={styles['drawer-wrap']}>
          <div className={styles['drawer-header']}>
            <Text variant="text-large-bold">{categorySelected?.title}</Text>
            <button
              className={styles['drawer-header-button']}
              type="button"
              onClick={onUnselectCategory}
            >
              <CloseIcon />
            </button>
          </div>

          <div className={styles['drawer-form']}>
            <LevelForm
              profile={profile}
              answers={answers}
              questions={questions}
              onHandleAnswers={onHandleAnswers}
              translations={{
                uploadSize: t('sidebarUploadSize'),
                uploadDrag: t('sidebarUploadDrag'),
                uploadButton: t('sidebarUploadButton'),
              }}
            />
          </div>

          <div className={styles['drawer-footer']}>
            <Button type="button" onClick={onUnselectCategory}>
              {t('drawerQuestionFooterButtonSubmit')}
            </Button>
          </div>
        </div>
      )}
    </Drawer>
  )
}
